import type {ImageField, KeyTextField, RichTextField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import type {FC} from 'react'
import {Tag} from './Tag'

type BentoGridHeaderProps = {
  tag?: Array<{icon?: ImageField; text?: KeyTextField}>
  heading?: RichTextField
  description?: KeyTextField
  leftAlignment?: boolean
  isDark?: boolean
}

/**
 * BentoGridHeader - Shared header component for all BentoGrid variations
 *
 * Features:
 * - Optional tag with icon
 * - Heading with support for H2 and strong tags
 * - Optional description text
 * - Centered or left-aligned layout
 */
export const BentoGridHeader: FC<BentoGridHeaderProps> = ({
  tag,
  heading,
  description,
  leftAlignment = false,
  isDark = false,
}) => (
  <div
    className={cn('mb-6 flex flex-col gap-4 md:mb-16 md:gap-6', {
      'items-center text-center': !leftAlignment,
      'items-start text-left': leftAlignment,
    })}
  >
    {/* Tag */}
    {tag && tag.length > 0 && tag[0] && <Tag icon={tag[0].icon} text={tag[0].text} />}

    {/* Heading */}
    {isFilled.richText(heading) && (
      <div className="max-md:px-[2px]">
        <PrismicRichText
          field={heading}
          components={{
            heading2: ({children}) => (
              <h2
                className={cn(
                  'md:text-h2 text-28 font-medium leading-8 max-md:tracking-[-.28px]',
                  isDark ? 'text-moss-white' : 'text-moss-black',
                )}
              >
                {children}
              </h2>
            ),
            strong: ({children}) => (
              <strong className={cn('font-medium', isDark ? 'text-moss-white/60' : 'text-moss-black/[60%]')}>
                {children}
              </strong>
            ),
          }}
        />
      </div>
    )}

    {isFilled.keyText(description) && (
      <p
        className={cn('md:text-tg-body-l max-w-[600px] text-16', isDark ? 'text-moss-white/70' : 'text-moss-grey-550')}
      >
        {description}
      </p>
    )}
  </div>
)
